// src/hooks/consult/useSilentMicDetector.ts

import { useEffect, useRef, useState } from "react";

// On AudioStreamingService.getInputLevel()'s scale; digital silence reads exactly 0. UNCALIBRATED: S10 measures a real quiet room on dev.
export const SILENT_LEVEL_FLOOR = 0.05;
export const SILENT_WINDOW_MS = 10_000;
export const SILENT_SAMPLE_MS = 250;

export type SilentMicReason = "level" | "track_muted";
export type SilentMicTransitionReason = SilentMicReason | "cleared";
export type SilentMicTransitionState = "entered" | "left";

export interface UseSilentMicDetectorOptions {
	enabled: boolean;
	getLevel: () => number | null | undefined;
	getTrack: () => MediaStreamTrack | null | undefined;
	onTransition?: (state: SilentMicTransitionState, reason: SilentMicTransitionReason) => void;
}

export interface SilentMicState {
	silent: boolean;
	reason: SilentMicReason | null;
}

const NOT_SILENT: SilentMicState = { silent: false, reason: null };

const isEligible = (track: MediaStreamTrack | null): track is MediaStreamTrack => !!track && track.readyState === "live" && track.enabled !== false;

export function useSilentMicDetector(options: UseSilentMicDetectorOptions): SilentMicState {
	const optionsRef = useRef(options);
	optionsRef.current = options;
	const [state, setState] = useState<SilentMicState>(NOT_SILENT);

	useEffect(() => {
		if (!options.enabled) return;
		let silentReason: SilentMicReason | null = null;
		let silentSince: number | null = null;
		let track: MediaStreamTrack | null = null;

		const transition = (next: SilentMicReason | null, reason: SilentMicTransitionReason) => {
			if ((next === null) === (silentReason === null)) return;
			silentReason = next;
			setState(next ? { silent: true, reason: next } : NOT_SILENT);
			optionsRef.current.onTransition?.(next ? "entered" : "left", reason);
		};

		const onMute = () => {
			if (isEligible(track)) transition("track_muted", "track_muted");
		};
		const onUnmute = () => {
			silentSince = null;
			transition(null, "track_muted");
		};

		const bind = (next: MediaStreamTrack | null) => {
			if (next === track) return;
			track?.removeEventListener("mute", onMute);
			track?.removeEventListener("unmute", onUnmute);
			track = next;
			silentSince = null;
			track?.addEventListener("mute", onMute);
			track?.addEventListener("unmute", onUnmute);
		};

		const sample = () => {
			bind(optionsRef.current.getTrack() ?? null);
			if (!isEligible(track)) {
				silentSince = null;
				transition(null, "cleared");
				return;
			}
			if (track.muted) {
				silentSince = null;
				transition("track_muted", "track_muted");
				return;
			}
			const level = document.hidden ? null : optionsRef.current.getLevel();
			// A skipped sample is not silence, so it breaks the run: entering needs a full window of observed silence.
			if (typeof level !== "number" || !Number.isFinite(level)) {
				silentSince = null;
				return;
			}
			if (level > SILENT_LEVEL_FLOOR) {
				silentSince = null;
				transition(null, "level");
				return;
			}
			const now = Date.now();
			if (silentSince === null) silentSince = now;
			if (now - silentSince >= SILENT_WINDOW_MS) transition("level", "level");
		};

		sample();
		const timer = setInterval(sample, SILENT_SAMPLE_MS);
		return () => {
			clearInterval(timer);
			bind(null);
			transition(null, "cleared");
		};
	}, [options.enabled]);

	return state;
}
